/**
 * Utility functions for formatting author dates
 */

/**
 * Formats a date string that can be:
 * - "YYYY-MM-DD" (exact date)
 * - "YYYY" (year only)
 * - "-YYYY" (year BCE)
 * - null/empty (unknown)
 *
 * @param {string} dateStr - The date string to format
 * @param {object} options - Formatting options
 * @param {boolean} options.full - If true, shows full date format. If false, shows abbreviated format
 * @returns {string} - Formatted date string
 */
export const formatAuthorDate = (dateStr, options = { full: true }) => {
    if (!dateStr) {
        return '?';
    }

    // Check if it's a negative year (BCE)
    if (/^-\d+$/.test(dateStr)) {
        return `${dateStr.substring(1)} př. n. l.`;
    }

    // Check if it's just a year (4 digits)
    if (/^\d{4}$/.test(dateStr)) {
        return dateStr;
    }

    // Otherwise it's a full date
    try {
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) {
            return '?';
        }

        if (options.full) {
            return date.toLocaleDateString('cs-CZ', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
            });
        } else {
            return date.getFullYear().toString();
        }
    } catch (err) {
        return '?';
    }
};

/**
 * Formats author lifespan (birth - death)
 *
 * @param {string} birthDate - Birth date string
 * @param {string} deathDate - Death date string
 * @param {boolean} full - If true, shows full dates. If false, shows years only
 * @returns {string} - Formatted lifespan string
 */
export const formatAuthorLifespan = (birthDate, deathDate, full = true) => {
    const birth = formatAuthorDate(birthDate, { full });
    const death = deathDate ? formatAuthorDate(deathDate, { full }) : '';

    if (death) {
        return `${birth} - ${death}`;
    } else if (birthDate) {
        return `${birth} - dosud`;
    } else {
        return 'Neznámé datum narození';
    }
};
